import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

// Loads the Cloudflare script once per page; resolves when window.turnstile exists.
const loadScript = () =>
  new Promise((resolve) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    let script = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', () => resolve(), { once: true });
  });

// Cloudflare Turnstile widget (managed mode — the widget config in the
// Cloudflare dashboard decides visibility). Tokens are single-use and expire
// after ~5 minutes: callers must reset() after every failed submit. When the
// site key env is absent the component renders nothing and never blocks —
// mirrors the api's Turnstile:Enabled flag for local dev.
// onInteractive and onError are optional and only reach Cloudflare when a
// caller supplies them, so the login pages render exactly as before. The
// manager app's bridge page (views/turnstile-bridge) is the caller that does.
const Turnstile = forwardRef(({ onToken, onInteractive, onError, className }, ref) => {
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const onInteractiveRef = useRef(onInteractive);
  onInteractiveRef.current = onInteractive;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
      onTokenRef.current(null);
    },
  }));

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;

    loadScript().then(() => {
      if (cancelled || !containerRef.current || widgetIdRef.current !== null) {
        return;
      }
      const params = {
        sitekey: siteKey,
        theme: 'auto',
        callback: (token) => onTokenRef.current(token),
        'expired-callback': () => onTokenRef.current(null),
      };
      if (onInteractiveRef.current) {
        params['before-interactive-callback'] = () => {
          if (onInteractiveRef.current) onInteractiveRef.current();
        };
      }
      if (onErrorRef.current) {
        // Returning true tells Cloudflare the error was handled, which is
        // what a caller that asked to hear about it means.
        params['error-callback'] = (code) => {
          if (onErrorRef.current) onErrorRef.current(code);
          return true;
        };
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, params);
    });

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={containerRef} className={className} />;
});

Turnstile.displayName = 'Turnstile';

export default Turnstile;
